import React, { useState, useMemo } from 'react';
import { Star, Trophy, TrendingUp, Users, Heart, MessageCircle, Share2, Bookmark, Zap, Loader2, Filter } from 'lucide-react';
import { useGetSuperFansQuery, useGetSuperFanStatsQuery, useGetSuperFanBreakdownQuery, SuperFanTier } from 'graphql/generated/graphql';

// Utility function to format numbers (1.2K, 1.5M, etc.)
const formatNumber = (num: number): string => {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B'
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K'
  }
  return num.toString()
}

// Format relative time
const getRelativeTime = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Never'
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return 'Just now'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)}w ago`
  return `${Math.floor(diffInSeconds / 2592000)}mo ago`
}

// Tier badge component
const TierBadge = ({ tier }: { tier: SuperFanTier }) => {
  const tierConfig: Record<SuperFanTier, { color: string, icon: React.ReactNode, label: string }> = {
    [SuperFanTier.MegaFan]: {
      color: 'bg-gradient-to-r from-purple-500 to-pink-500',
      icon: <Trophy className="w-3 h-3" />,
      label: 'Mega Fan'
    },
    [SuperFanTier.SuperFan]: {
      color: 'bg-gradient-to-r from-blue-500 to-cyan-500',
      icon: <Star className="w-3 h-3" />,
      label: 'Super Fan'
    },
    [SuperFanTier.ActiveFan]: {
      color: 'bg-gradient-to-r from-green-500 to-emerald-500',
      icon: <Zap className="w-3 h-3" />,
      label: 'Active Fan'
    },
    [SuperFanTier.CasualFan]: {
      color: 'bg-gradient-to-r from-gray-400 to-gray-500',
      icon: <Users className="w-3 h-3" />,
      label: 'Casual Fan'
    },
  }

  const config = tierConfig[tier]

  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${config.color} text-white text-xs font-semibold`}>
      {config.icon}
      {config.label}
    </div>
  )
}

// Platform icon
const PlatformIcon = ({ platform }: { platform: string }) => {
  const platformColors: Record<string, string> = {
    Instagram: 'from-pink-500 to-purple-600',
    Facebook: 'from-blue-600 to-blue-700',
    TikTok: 'from-black to-cyan-400',
    Youtube: 'from-red-600 to-red-700',
    Reddit: 'from-orange-500 to-orange-600',
  };

  const platformInitials: Record<string, string> = {
    Instagram: 'IG',
    Facebook: 'FB',
    TikTok: 'TT',
    Youtube: 'YT',
    Reddit: 'RD',
  };

  return (
    <div className={`w-8 h-8 bg-gradient-to-br ${platformColors[platform]} rounded-full flex items-center justify-center text-white font-bold text-[10px]`}>
      {platformInitials[platform]}
    </div>
  );
};

export default function SuperFansPage() {
  const [selectedTier, setSelectedTier] = useState<SuperFanTier | 'all'>('all');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch data
  const { data: statsData, loading: statsLoading } = useGetSuperFanStatsQuery({
    fetchPolicy: 'network-only',
  });

  const { data: fansData, loading: fansLoading } = useGetSuperFansQuery({
    variables: {
      limit: 100,
      filters: selectedTier !== 'all' ? { tier: selectedTier } : undefined,
    },
    fetchPolicy: 'network-only',
  });

  const { data: breakdownData } = useGetSuperFanBreakdownQuery({
    fetchPolicy: 'network-only',
  });

  const stats = statsData?.getSuperFanStats;
  const fans = fansData?.getSuperFans ?? [];
  const breakdown = breakdownData?.getSuperFanBreakdown ?? [];

  // Filter fans based on search and platform
  const filteredFans = useMemo(() => {
    return fans.filter(fan => {
      const matchesSearch = searchQuery === '' ||
        fan.followerUsername.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (fan.followerDisplayName?.toLowerCase() ?? '').includes(searchQuery.toLowerCase());

      const matchesPlatform = selectedPlatform === 'all' || fan.platform === selectedPlatform;

      return matchesSearch && matchesPlatform;
    });
  }, [fans, searchQuery, selectedPlatform]);

  const platforms = ['all', 'Instagram', 'TikTok', 'Youtube', 'Facebook'];

  if (statsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-500 mx-auto mb-4" />
          <p style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Loading your super fans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Star className="w-6 h-6 text-white" />
            </div>
            Super Fans
          </h1>
          <p style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Identify and engage with your most loyal followers</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="rounded-xl p-6 border transition-all duration-300 hover:transform hover:-translate-y-0.5" style={{
            background: 'rgba(15, 23, 42, 0.8)',
            borderColor: 'rgba(168, 85, 247, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(168, 85, 247, 0.2)' }}>
                <Trophy className="w-5 h-5 text-purple-400" />
              </div>
              <span className="text-sm font-medium text-purple-400">Top 5%</span>
            </div>
            <h3 className="text-2xl font-bold" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{stats?.megaFans ?? 0}</h3>
            <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Mega Fans</p>
          </div>

          <div className="rounded-xl p-6 border transition-all duration-300 hover:transform hover:-translate-y-0.5" style={{
            background: 'rgba(15, 23, 42, 0.8)',
            borderColor: 'rgba(168, 85, 247, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(59, 130, 246, 0.2)' }}>
                <Star className="w-5 h-5 text-blue-400" />
              </div>
              <span className="text-sm font-medium text-blue-400">Top 20%</span>
            </div>
            <h3 className="text-2xl font-bold" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{stats?.superFans ?? 0}</h3>
            <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Super Fans</p>
          </div>

          <div className="rounded-xl p-6 border transition-all duration-300 hover:transform hover:-translate-y-0.5" style={{
            background: 'rgba(15, 23, 42, 0.8)',
            borderColor: 'rgba(168, 85, 247, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(34, 197, 94, 0.2)' }}>
                <Zap className="w-5 h-5 text-green-400" />
              </div>
              <span className="text-sm font-medium text-green-400">Top 50%</span>
            </div>
            <h3 className="text-2xl font-bold" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{stats?.activeFans ?? 0}</h3>
            <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Active Fans</p>
          </div>

          <div className="rounded-xl p-6 border transition-all duration-300 hover:transform hover:-translate-y-0.5" style={{
            background: 'rgba(15, 23, 42, 0.8)',
            borderColor: 'rgba(168, 85, 247, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <div className="flex items-center justify-between mb-2">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                <Users className="w-5 h-5" style={{ color: 'rgba(255, 255, 255, 0.6)' }} />
              </div>
            </div>
            <h3 className="text-2xl font-bold" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{stats?.totalSuperFans ?? 0}</h3>
            <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Total Tracked</p>
          </div>
        </div>

        {/* Average Scores */}
        <div className="rounded-xl p-6 border mb-8" style={{
          background: 'rgba(15, 23, 42, 0.8)',
          borderColor: 'rgba(168, 85, 247, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Average Engagement Scores</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Consistency</span>
                <span className="text-sm font-semibold" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{stats?.avgConsistencyScore.toFixed(1) ?? 0}/100</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                <div
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-300"
                  style={{ width: `${stats?.avgConsistencyScore ?? 0}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Depth</span>
                <span className="text-sm font-semibold" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{stats?.avgDepthScore.toFixed(1) ?? 0}/100</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all duration-300"
                  style={{ width: `${stats?.avgDepthScore ?? 0}%` }}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Recency</span>
                <span className="text-sm font-semibold" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{stats?.avgRecencyScore.toFixed(1) ?? 0}/100</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                <div
                  className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-300"
                  style={{ width: `${stats?.avgRecencyScore ?? 0}%` }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="rounded-xl p-6 border mb-6" style={{
          background: 'rgba(15, 23, 42, 0.8)',
          borderColor: 'rgba(168, 85, 247, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search by username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderColor: 'rgba(168, 85, 247, 0.3)',
                  color: 'rgba(255, 255, 255, 0.9)'
                }}
              />
            </div>

            {/* Tier Filter */}
            <select
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value as SuperFanTier | 'all')}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderColor: 'rgba(168, 85, 247, 0.3)',
                color: 'rgba(255, 255, 255, 0.9)'
              }}
            >
              <option value="all">All Tiers</option>
              <option value={SuperFanTier.MegaFan}>Mega Fans</option>
              <option value={SuperFanTier.SuperFan}>Super Fans</option>
              <option value={SuperFanTier.ActiveFan}>Active Fans</option>
              <option value={SuperFanTier.CasualFan}>Casual Fans</option>
            </select>

            {/* Platform Filter */}
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderColor: 'rgba(168, 85, 247, 0.3)',
                color: 'rgba(255, 255, 255, 0.9)'
              }}
            >
              {platforms.map(platform => (
                <option key={platform} value={platform}>
                  {platform === 'all' ? 'All Platforms' : platform}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Fans List */}
        <div className="rounded-xl border overflow-hidden" style={{
          background: 'rgba(15, 23, 42, 0.8)',
          borderColor: 'rgba(168, 85, 247, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <div className="p-6 border-b" style={{ borderColor: 'rgba(168, 85, 247, 0.2)' }}>
            <h3 className="text-lg font-semibold" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Your Super Fans ({filteredFans.length})
            </h3>
          </div>

          {fansLoading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-purple-500 mx-auto mb-3" />
              <p style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Loading fans...</p>
            </div>
          ) : filteredFans.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 mx-auto mb-3" style={{ color: 'rgba(255, 255, 255, 0.3)' }} />
              <p style={{ color: 'rgba(255, 255, 255, 0.6)' }}>No super fans found</p>
              <p className="text-sm mt-1" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                {fans.length === 0
                  ? 'Sync your post engagement data to start tracking super fans'
                  : 'Try adjusting your filters'}
              </p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: 'rgba(168, 85, 247, 0.1)' }}>
              {filteredFans.map((fan) => (
                <div key={fan.id} className="p-6 transition-colors" style={{
                  borderColor: 'rgba(168, 85, 247, 0.1)'
                }} onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(168, 85, 247, 0.05)'
                }} onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent'
                }}>
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {fan.followerAvatarUrl ? (
                        <img
                          src={fan.followerAvatarUrl}
                          alt={fan.followerUsername}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-white font-bold text-lg">
                          {fan.followerUsername.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold truncate" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                          {fan.followerDisplayName ?? fan.followerUsername}
                        </h4>
                        <TierBadge tier={fan.tier} />
                        <PlatformIcon platform={fan.platform} />
                      </div>

                      <p className="text-sm mb-3" style={{ color: 'rgba(255, 255, 255, 0.6)' }}>@{fan.followerUsername}</p>

                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div className="flex items-center gap-2">
                          <Heart className="w-4 h-4 text-pink-500" />
                          <span className="text-sm font-medium" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{formatNumber(fan.totalLikes)}</span>
                          <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>likes</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MessageCircle className="w-4 h-4 text-blue-500" />
                          <span className="text-sm font-medium" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{formatNumber(fan.totalComments)}</span>
                          <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>comments</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Share2 className="w-4 h-4 text-green-500" />
                          <span className="text-sm font-medium" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{formatNumber(fan.totalShares)}</span>
                          <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>shares</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Bookmark className="w-4 h-4 text-purple-500" />
                          <span className="text-sm font-medium" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{formatNumber(fan.totalSaves)}</span>
                          <span className="text-xs" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>saves</span>
                        </div>
                      </div>

                      {/* Scores */}
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <div className="text-xs mb-1" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Consistency</div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                              <div
                                className="h-full bg-purple-500 rounded-full"
                                style={{ width: `${fan.consistencyScore}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{fan.consistencyScore.toFixed(0)}</span>
                          </div>
                        </div>
                        <div>
                          <div className="text-xs mb-1" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Depth</div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                              <div
                                className="h-full bg-blue-500 rounded-full"
                                style={{ width: `${fan.depthScore}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{fan.depthScore.toFixed(0)}</span>
                          </div>
                        </div>
                        <div>
                          <div className="text-xs mb-1" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>Recency</div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                              <div
                                className="h-full bg-green-500 rounded-full"
                                style={{ width: `${fan.recencyScore}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>{fan.recencyScore.toFixed(0)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between mt-4 pt-4 border-t" style={{ borderColor: 'rgba(168, 85, 247, 0.2)' }}>
                        <div className="flex items-center gap-4 text-xs" style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                          <span>Last interaction: {getRelativeTime(fan.lastInteraction)}</span>
                          <span>•</span>
                          <span>Fan for {fan.daysSinceFirst} days</span>
                        </div>
                        <div className="text-sm font-semibold text-purple-400">
                          Score: {fan.overallScore.toFixed(1)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
